// useDayNotes — read + mutate diary day notes.
//
// Reads: `useWeekNotes(driverId, weekStart)` fetches notes for the entire
// week from `GET /api/v1/collections/diary/notes?driver_id=&date_from=&date_to=`.
// (We could now also pull notes from `DiaryWeeklyResponse.days[].notes`
//  since the server bug at HANDOFF gap §13 was fixed — see server's
//  diary.service.ts dateToYmd. The dedicated /diary/notes hook is kept
//  as a clean separation: notes fetch independently from the bookings
//  panel and don't get clobbered by reorder refetches.)
//
// Mutations (add / update / delete) invalidate BOTH `diary.week` (so the
// bookings panel refreshes) and `diary.weekNotes` (so the notes section
// refreshes).
//
// Server contracts (verified against server/HANDOFF.md + diary.types.ts):
//   GET    /api/v1/collections/diary/notes?driver_id=&date_from=&date_to=
//                                    -> { notes: DiaryNoteDto[] }
//   POST   /api/v1/collections/diary/notes       { driver_id, date, note_text, note_type }
//   PUT    /api/v1/collections/diary/notes/:enc_id  { note_text?, note_type? }
//   DELETE /api/v1/collections/diary/notes/:enc_id  (204 No Content)
//
// Note text upper bound on the server is 2000 chars; the UI caps at 500
// per the legacy spec (UI_AUDIT §2.3). Note types are 'note' | 'alert'.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import { useAuthStore } from '../../../store/authStore';
import type { DiaryNoteDto, DiaryNoteType } from '../../../types/api';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const PLACEHOLDER_DATE = '0000-00-00';

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Returns the Sunday of the week starting on `weekStart` (Mon → Sun). */
function weekEndOf(weekStart: string): string {
  if (!ISO_DATE_RE.test(weekStart)) return weekStart;
  const [y, m, d] = weekStart.split('-').map(Number);
  const dt = new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
  dt.setDate(dt.getDate() + 6);
  return isoDate(dt);
}

function paramsAreValid(driverId: number, weekStart: string): boolean {
  return (
    Number.isInteger(driverId) &&
    driverId > 0 &&
    typeof weekStart === 'string' &&
    weekStart !== PLACEHOLDER_DATE &&
    ISO_DATE_RE.test(weekStart)
  );
}

export interface AddNoteInput {
  driverId: number;
  date: string; // YYYY-MM-DD
  noteText: string;
  noteType: DiaryNoteType;
}

export interface UpdateNoteInput {
  encId: string;
  noteText: string;
}

export interface UseDayNotesArgs {
  driverId: number;
  weekStart: string;
}

export interface UseWeekNotesArgs {
  driverId: number;
  weekStart: string;
}

/**
 * Fetches every diary note for one driver across one Mon–Sun week.
 * Source of truth for notes on the diary results page (the weekly
 * /diary endpoint's embedded notes arrays are unreliable — see HANDOFF).
 */
export function useWeekNotes({ driverId, weekStart }: UseWeekNotesArgs) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return useQuery<DiaryNoteDto[]>({
    queryKey: queryKeys.diary.weekNotes(driverId, weekStart),
    queryFn: async () => {
      if (!paramsAreValid(driverId, weekStart)) {
        throw new Error(
          `useWeekNotes called with invalid params: driverId=${driverId}, weekStart=${weekStart}`,
        );
      }
      const res = await apiClient.get<{ notes: DiaryNoteDto[] }>(endpoints.diary.notes, {
        params: {
          driver_id: driverId,
          date_from: weekStart,
          date_to: weekEndOf(weekStart),
        },
      });
      return res.data.notes;
    },
    enabled: isAuthenticated && paramsAreValid(driverId, weekStart),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

export function useDayNotes({ driverId, weekStart }: UseDayNotesArgs) {
  const queryClient = useQueryClient();

  const invalidateWeek = async () => {
    // Invalidate BOTH the bookings panel (`diary.week`) AND the notes
    // panel (`diary.weekNotes`). The server bug means the weekly
    // endpoint's embedded notes are unreliable, so we rely on the
    // dedicated weekNotes query — which must refetch after every mutation.
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: queryKeys.diary.week(driverId, weekStart),
      }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.diary.weekNotes(driverId, weekStart),
      }),
    ]);
  };

  const addNote = useMutation({
    mutationFn: async (input: AddNoteInput): Promise<DiaryNoteDto> => {
      const res = await apiClient.post<{ note: DiaryNoteDto }>(endpoints.diary.notes, {
        driver_id: input.driverId,
        date: input.date,
        note_text: input.noteText,
        note_type: input.noteType,
      });
      return res.data.note;
    },
    onSuccess: () => {
      void invalidateWeek();
    },
  });

  const updateNote = useMutation({
    mutationFn: async (input: UpdateNoteInput): Promise<DiaryNoteDto> => {
      const res = await apiClient.put<{ note: DiaryNoteDto }>(
        endpoints.diary.note(input.encId),
        { note_text: input.noteText },
      );
      return res.data.note;
    },
    onSuccess: () => {
      void invalidateWeek();
    },
  });

  const deleteNote = useMutation({
    mutationFn: async (encId: string): Promise<void> => {
      await apiClient.raw.delete(endpoints.diary.note(encId));
    },
    onSuccess: () => {
      void invalidateWeek();
    },
  });

  return {
    addNote,
    updateNote,
    deleteNote,
  };
}
